/* =====================================================
   afk-audio — product page gallery + buy-box labels
   Gallery: on phones the main image is a native swipe
   track (scroll-snap) with a "1 / N" counter; on every
   width a thumbnail jumps straight to its slide.
   Buy box: keeps the phone-only price on the add-to-cart
   label and the sticky bar's title in step with the
   quantity and ticked add-ons. theme.js still owns the
   variant, total, stepper and add-to-cart logic.
   Jump links: reveals the phone shortcuts to the press
   quotes and the long sections below the buy box.
   Self-contained; guarded against double-init.
   ===================================================== */
(function () {
  'use strict';
  if (window.__afkGallery) return;
  window.__afkGallery = true;

  var SETTINGS = window.AFK || {};

  // same output as formatMoney in theme.js (kept local so this file stands alone)
  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    var format = SETTINGS.moneyFormat || '€{{amount}}';
    var parts = amount.split('.');
    var comma = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + parts[1];
    var dot = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + parts[1];
    return format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, comma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, parts[0])
      .replace(/\{\{\s*amount\s*\}\}/g, dot)
      .replace(/[.,]00(?!\d)/, '');
  }

  /* ---------- gallery ---------- */
  function initGallery(root) {
    var track = root.querySelector('[data-gallery-track]');
    if (!track || track.dataset.afkInit) return;
    track.dataset.afkInit = '1';
    var slides = track.children;
    var thumbs = root.querySelectorAll('[data-pdp-thumb]');
    var strip = root.querySelector('.pdp__thumbs');
    var count = root.querySelector('[data-gallery-count]');
    var n = slides.length, cur = 0, ticking = false;

    // start fetching the next slide before the finger gets there
    function warm(i) {
      var img = slides[i] && slides[i].querySelector('img[loading="lazy"]');
      if (img) img.loading = 'eager';
    }

    function mark(i) {
      if (i === cur) return;
      cur = i;
      for (var k = 0; k < thumbs.length; k++) {
        thumbs[k].classList.toggle('is-active', k === i);
        if (k === i) thumbs[k].setAttribute('aria-current', 'true'); else thumbs[k].removeAttribute('aria-current');
      }
      if (count) count.textContent = (i + 1) + ' / ' + n;
      warm(i + 1);
      // keep the active thumb visible by scrolling the strip only (never the page)
      var t = thumbs[i];
      if (strip && t) {
        var sr = strip.getBoundingClientRect(), tr = t.getBoundingClientRect();
        if (tr.left < sr.left) strip.scrollLeft += tr.left - sr.left;
        else if (tr.right > sr.right) strip.scrollLeft += tr.right - sr.right;
      }
    }

    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        var w = track.clientWidth;
        if (w) mark(Math.max(0, Math.min(n - 1, Math.round(track.scrollLeft / w))));
      });
    }, { passive: true });
    track.addEventListener('touchstart', function () { warm(cur + 1); }, { passive: true });

    Array.prototype.forEach.call(thumbs, function (t) {
      t.addEventListener('click', function () {
        var i = parseInt(t.dataset.index, 10) || 0, s = slides[i];
        if (!s) return;
        // offset from the rects, so fractional slide widths never leave a sliver of the next image
        track.scrollLeft += s.getBoundingClientRect().left - track.getBoundingClientRect().left;
        mark(i);
      });
    });
  }

  /* ---------- buy-box labels ---------- */
  function initBuyBox(root) {
    var form = root.querySelector('form[data-ajax-cart]');
    var dataEl = root.querySelector('[data-product-json]');
    if (!form || !dataEl || form.dataset.afkLabels) return;
    form.dataset.afkLabels = '1';
    var product;
    try { product = JSON.parse(dataEl.textContent); } catch (e) { return; }
    var idInput = form.querySelector('[name="id"]');
    var qtyInput = form.querySelector('[name="quantity"]');
    var atc = form.querySelector('[type="submit"][data-atc-price]');
    var bar = document.querySelector('.sticky-atc');
    var title = bar && bar.querySelector('.sticky-atc__title');
    var titleHtml = title ? title.innerHTML : '';
    var base = title && title.firstChild ? (title.firstChild.nodeValue || '').trim() : '';
    var last = '';

    // the phone label drops its price when it would not fit (narrow row next to the stepper, long translations)
    function fit() {
      if (!atc) return;
      atc.classList.remove('is-tight');
      var have = atc.offsetWidth;
      atc.style.width = 'max-content';
      var need = atc.offsetWidth;
      atc.style.width = '';
      if (need > have + 1) atc.classList.add('is-tight');
    }

    function sync() {
      var v = null;
      for (var k = 0; k < product.variants.length; k++) {
        if (idInput && String(product.variants[k].id) === idInput.value) v = product.variants[k];
      }
      if (!v) return;
      var qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
      var total = v.price * qty, names = [];
      form.querySelectorAll('[data-addon-id]:checked').forEach(function (a) {
        total += parseInt(a.dataset.addonPrice || '0', 10);
        if (a.dataset.addonTitle) names.push(a.dataset.addonTitle);
      });
      if (atc) { atc.setAttribute('data-atc-price', formatMoney(total)); fit(); }

      // sticky title: as rendered (unit price) by default; a labelled total once quantity or an add-on changes it
      if (!title || !base) return;
      var changed = qty > 1 || names.length > 0;
      var key = changed ? qty + '|' + names.join('+') + '|' + total : '';
      if (key === last) return;
      last = key;
      if (!changed) { title.innerHTML = titleHtml; return; }
      // name and price in their own spans, so on phones only the name gets cut when space runs out
      var name = document.createElement('span'), price = document.createElement('span');
      name.className = 'sticky-atc__name';
      name.textContent = (qty > 1 ? qty + ' × ' : '') + base + (names.length ? ' + ' + names.join(' + ') : '');
      price.className = 'text-dim';
      price.textContent = '· ' + formatMoney(total);
      title.textContent = '';
      title.appendChild(name);
      title.appendChild(document.createTextNode(' '));
      title.appendChild(price);
    }

    form.addEventListener('change', sync);
    form.addEventListener('input', sync);
    // theme.js's stepper handlers sit on the buttons themselves, so the new quantity is in by now
    form.addEventListener('click', function (e) { if (e.target.closest && e.target.closest('[data-qty-btn]')) sync(); });
    window.addEventListener('pageshow', sync);
    var rt = 0;
    window.addEventListener('resize', function () { clearTimeout(rt); rt = setTimeout(fit, 150); }, { passive: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);
    sync();
  }

  /* ---------- jump links (phones) ----------
     The press link under the subtitle and the chips after 'in the box'
     point at sections further down. Each shows only when its section is on
     the page; a section without an id gets one. Idempotent, so it simply
     runs again after an editor re-render.                               */
  var JUMPS = {
    press: '[data-section="press-quotes"]',
    ports: '[data-section="ports-setups"], [data-section="setups"], [data-section="ports"]',  // the first of them on the page
    mounting: '[data-section="mounting"]',
    specs: '[data-section="specs-table"]',
    faq: '[data-section="faq"]'
  };
  function initJumps(root) {
    root.querySelectorAll('[data-pdp-jump]').forEach(function (a) {
      var key = a.getAttribute('data-pdp-jump');
      var t = JUMPS[key] && document.querySelector(JUMPS[key]);
      if (t) {
        if (!t.id) t.id = key;
        a.href = '#' + t.id;
      }
      a.hidden = !t;
    });
    root.querySelectorAll('.pdp__jump').forEach(function (nav) {
      nav.hidden = !nav.querySelector('a:not([hidden])');
    });
  }

  function boot() {
    document.querySelectorAll('[data-product-root]').forEach(function (root) {
      try { initGallery(root); } catch (e) { if (window.console) console.error('[pdp-gallery] gallery', e); }
      try { initBuyBox(root); } catch (e) { if (window.console) console.error('[pdp-gallery] buy box', e); }
      try { initJumps(root); } catch (e) { if (window.console) console.error('[pdp-gallery] jumps', e); }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
